import { motion } from 'framer-motion';
import { OutlineChevronDownIcon } from '@tempmee/react-icons';
import { colors } from '@tempmee/tailwind';
import { Colors } from '~/themes/colors';
import { ClickAwayListener } from '../ClickAwayListener';
import { Pressable } from '../Pressable';
import { Row } from '../Row';
import { useAutocomplete } from './hooks';
import { IOptionValue, TFilterOptionCallback, TValueChangeCallback } from './interfaces';
import { OptionList } from './OptionList';

import './styles.scss';

interface IProps<T> {
  options: IOptionValue<T>[];
  maxSuggestions?: number;
  placeholder?: string;
  value?: T;
  filterOptions?: TFilterOptionCallback<T>;
  onChange: TValueChangeCallback<T>;
  error?: boolean;
}

export const Autocomplete = <T,>({
  filterOptions,
  maxSuggestions,
  onChange,
  options,
  placeholder = 'Search',
  value,
  error = false,
}: IProps<T>) => {
  const {
    data,
    handleCloseOptions,
    handleOpenOptions,
    isSearching,
    isShowOptions,
    onValueChanges,
    refs,
  } = useAutocomplete({
    filterOptions,
    onChange,
    options,
    maxSuggestions,
    value,
  });

  return (
    <ClickAwayListener className="autocomplete" onClickAway={handleCloseOptions}>
      <Row
        alignItems="center"
        className="input-field"
        width="auto"
        paddingRight={8}
        style={{
          cursor: 'pointer',
          ...(error && {
            borderWidth: 1.5,
            borderStyle: 'solid',
            borderColor: Colors.error_400,
            backgroundColor: Colors.error_background,
          }),
        }}
      >
        <input
          ref={refs.input}
          placeholder={placeholder}
          style={{ all: 'unset', flex: 1 }}
          onFocus={handleOpenOptions}
          data-testid="autocomplete"
        />
        <Pressable
          onPress={handleOpenOptions}
          style={{
            height: 24,
            width: 24,
          }}
        >
          <OutlineChevronDownIcon size={24} color={colors.secondary[500]} />
        </Pressable>
      </Row>
      <motion.div
        ref={refs.options}
        animate={isShowOptions ? 'show' : 'hide'}
        className="options"
        initial="hide"
        transition={{ duration: 0.1 }}
        variants={{
          show: { opacity: 1, display: 'flex', zIndex: 1 },
          hide: { opacity: 0, transitionEnd: { display: 'none' } },
        }}
      >
        <OptionList data={data} isSearching={isSearching} onChange={onValueChanges} />
      </motion.div>
    </ClickAwayListener>
  );
};
